import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';

import { UserContext } from './hooks/UserContext';
import Header from './components/Header';

export const ManageOnboarding = () => {
    const [categories, setCategories] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [lists, setLists] = useState({});
    const [allCourses, setAllCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [publishing, setPublishing] = useState(false);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [publishError, setPublishError] = useState(null);
    const { user } = useContext(UserContext);

    const snapshotRef = useRef({});

    const headers = {
        'Authorization': `Bearer ${user.token}`,
        'X-Editor': 'nanoms',
        'Content-Type': 'application/json'
    };

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const authHeaders = {
                'Authorization': `Bearer ${user.token}`, 'X-Editor': 'nanoms'
            };
            const [recRes, coursesRes] = await Promise.all([
                axios.get('/api/onboarding/recommendations', { headers: authHeaders }),
                axios.get('/api/courses?per_page=500', { headers: authHeaders })
            ]);

            const { categories: cats, recommendations } = recRes.data;
            setCategories(cats);
            setAllCourses(coursesRes.data.items);

            const loaded = {};
            cats.forEach(cat => {
                loaded[cat.id] = recommendations[String(cat.id)] || [];
            });
            setLists(loaded);
            snapshotRef.current = loaded;
            setSelectedCategoryId(prev => prev ?? (cats[0]?.id ?? null));
            setPublishError(null);
        } catch (error) {
            console.error('Failed to load data:', error);
            alert('Failed to load onboarding recommendations');
        } finally {
            setLoading(false);
        }
    }, [user.token]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const courseById = (id) => allCourses.find(c => c.id === id);
    const currentList = lists[selectedCategoryId] || [];
    const currentRows = currentList
        .map(id => courseById(id) || { id, name: `(unknown course ${id})`, status: '?' });

    const dirtyCategoryIds = categories
        .map(c => c.id)
        .filter(id => {
            const a = lists[id] || [];
            const b = snapshotRef.current[id] || [];
            return a.length !== b.length || a.some((v, i) => v !== b[i]);
        });
    const isDirty = dirtyCategoryIds.length > 0;

    const setCurrentList = (ids) => {
        setLists(prev => ({ ...prev, [selectedCategoryId]: ids }));
    };

    const onRowReorder = (e) => {
        setCurrentList(e.value.map(row => row.id));
    };

    const addCourse = (courseId) => {
        if (currentList.includes(courseId)) {
            alert('Course is already in the list');
            return;
        }
        setCurrentList([...currentList, courseId]);
        setShowAddDialog(false);
        setSearchTerm('');
    };

    const removeCourse = (courseId) => {
        const course = courseById(courseId);
        if (!window.confirm(`Remove "${course?.name || courseId}" from this list?`)) return;
        setCurrentList(currentList.filter(id => id !== courseId));
    };

    const discardChanges = () => {
        setLists(snapshotRef.current);
        setPublishError(null);
    };

    const publish = async () => {
        setPublishing(true);
        setPublishError(null);
        try {
            for (const categoryId of dirtyCategoryIds) {
                await axios.put(
                    `/api/onboarding/recommendations/${categoryId}`,
                    { course_ids: lists[categoryId] },
                    { headers }
                );
            }
            await loadData();
        } catch (error) {
            console.error('Failed to publish:', error);
            setPublishError('Failed to publish changes. Please try again.');
        } finally {
            setPublishing(false);
        }
    };

    const nameBodyTemplate = (rowData) => {
        return <a href={`/editor?course=${rowData.id}`} target="_blank" rel="noopener noreferrer">{rowData.name}</a>;
    };

    const positionBodyTemplate = (_rowData, { rowIndex }) => {
        return rowIndex + 1;
    };

    // PrimeReact 6 passes only rowData here (no rowIndex options object).
    const rowClassName = (rowData) => {
        const snapshot = snapshotRef.current[selectedCategoryId] || [];
        if (!snapshot.includes(rowData.id)) return { 'row-added': true };
        if (snapshot.indexOf(rowData.id) !== currentList.indexOf(rowData.id)) {
            return { 'row-moved': true };
        }
        return {};
    };

    const removeBodyTemplate = (rowData) => {
        return (
            <Button
                icon="pi pi-trash"
                className="p-button-sm p-button-outlined p-button-danger"
                onClick={() => removeCourse(rowData.id)}
                tooltip="Remove from this list"
                tooltipOptions={{ position: 'top' }}
            />
        );
    };

    if (loading) {
        return (
            <div className="App">
                <Header />
                <div className="container-fluid mt-4">
                    <div className="text-center">
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                        <p>Loading...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (categories.length === 0) {
        return (
            <div className="App">
                <Header />
                <div className="container-fluid mt-4">
                    <div className="alert alert-warning">
                        <strong>Warning:</strong> No onboarding categories resolved on this server
                        (category names must exist in Manage Categories).
                    </div>
                </div>
            </div>
        );
    }

    const selectedCategory = categories.find(c => c.id === selectedCategoryId);

    return (
        <div className="App">
            <style>{`
                .row-added > td { background-color: rgba(25, 135, 84, 0.15) !important; }
                .row-moved > td { background-color: rgba(255, 193, 7, 0.15) !important; }
            `}</style>
            <Header />
            <div className="container-fluid">
                <div className="row mt-4">
                    <div className="col-12 text-center">
                        <h2 className="mb-3">Manage Onboarding Recommendations</h2>
                    </div>
                </div>

                <div className="row mt-2">
                    <div className="col-12 col-xl-10 mx-auto">
                        <div className="alert alert-info py-2 small mb-3" role="alert">
                            <strong>About onboarding recommendations:</strong>
                            <ul className="mb-0">
                                <li>Per-category seed lists for the onboarding "Save a few for later" screen</li>
                                <li>The app shows 5 (6 on tall screens); keep ~8 per list — the extra depth replaces courses already taken by another picked category</li>
                                <li>Order matters: rank 1 is offered first. Drag rows to reorder</li>
                                <li>"Publish" saves every changed category at once and refreshes the app config</li>
                                <li>The zero-picks fallback list is the Most Popular rail (Manage Popular), not managed here</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="row mt-3">
                    <div className="col-12 col-xl-10 mx-auto">
                        {isDirty && (
                            <div className="alert alert-warning py-2 mb-3 d-flex align-items-center gap-2">
                                <i className="pi pi-exclamation-triangle"></i>
                                <span>
                                    Unpublished changes in: {dirtyCategoryIds
                                        .map(id => categories.find(c => c.id === id)?.name || id)
                                        .join(', ')}
                                </span>
                            </div>
                        )}

                        {publishError && (
                            <div className="alert alert-danger py-2 mb-3">
                                {publishError}
                            </div>
                        )}

                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="d-flex align-items-center gap-2">
                                <strong>Category:</strong>
                                <Dropdown
                                    value={selectedCategoryId}
                                    options={categories.map(c => ({
                                        label: `${c.emoji} ${c.name} (${(lists[c.id] || []).length})`,
                                        value: c.id
                                    }))}
                                    onChange={(e) => setSelectedCategoryId(e.value)}
                                    style={{ minWidth: '280px' }}
                                />
                            </div>
                            <div className="d-flex gap-2">
                                <Button
                                    label="Add Course"
                                    icon="pi pi-plus"
                                    className="p-button-outlined"
                                    onClick={() => setShowAddDialog(true)}
                                    disabled={publishing}
                                />
                                <Button
                                    label="Refresh"
                                    icon="pi pi-refresh"
                                    className="p-button-outlined"
                                    onClick={loadData}
                                    disabled={publishing || isDirty}
                                    tooltip={isDirty ? 'Discard or publish changes first' : undefined}
                                />
                                <Button
                                    label="Discard Changes"
                                    icon="pi pi-undo"
                                    className="p-button-outlined p-button-secondary"
                                    onClick={discardChanges}
                                    disabled={!isDirty || publishing}
                                />
                                <div style={{ width: '3rem' }} />
                                <Button
                                    label="Publish"
                                    icon={publishing ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                                    className="p-button-info"
                                    onClick={publish}
                                    disabled={!isDirty || publishing}
                                />
                            </div>
                        </div>

                        {currentRows.length === 0 ? (
                            <div className="alert alert-warning">
                                No seed courses for {selectedCategory?.name}. Click "Add Course" to get started.
                            </div>
                        ) : (
                            <DataTable
                                value={currentRows}
                                responsiveLayout="scroll"
                                showGridlines
                                className="border"
                                reorderableRows
                                onRowReorder={onRowReorder}
                                rowClassName={rowClassName}
                            >
                                <Column rowReorder style={{ width: '3rem' }} />
                                <Column
                                    header="#"
                                    body={positionBodyTemplate}
                                    style={{ width: '60px' }}
                                />
                                <Column
                                    field="id"
                                    header="ID"
                                    style={{ width: '80px' }}
                                />
                                <Column
                                    field="name"
                                    header="Course Name"
                                    body={nameBodyTemplate}
                                    style={{ minWidth: '300px' }}
                                />
                                <Column
                                    field="status"
                                    header="Status"
                                    style={{ width: '120px' }}
                                />
                                <Column
                                    header=""
                                    body={removeBodyTemplate}
                                    style={{ width: '60px' }}
                                />
                            </DataTable>
                        )}
                    </div>
                </div>

                <Dialog
                    header={`Add Course to ${selectedCategory?.name || ''}`}
                    visible={showAddDialog}
                    style={{ width: '700px' }}
                    modal
                    onHide={() => {
                        setShowAddDialog(false);
                        setSearchTerm('');
                    }}
                >
                    <div className="mb-3">
                        <label className="form-label">Search Courses</label>
                        <InputText
                            type="text"
                            className="form-control"
                            placeholder="Search by name or ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="border rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {(() => {
                            const currentIds = new Set(currentList);
                            const availableCourses = allCourses.filter(course => {
                                return !currentIds.has(course.id);
                            });

                            const filtered = availableCourses.filter(course => {
                                if (!searchTerm) return true;
                                const search = searchTerm.toLowerCase();
                                return String(course.id).includes(search) ||
                                       (course.name || '').toLowerCase().includes(search);
                            });

                            if (filtered.length === 0) {
                                return (
                                    <div className="text-center text-muted py-4">
                                        {searchTerm ? 'No courses found matching search' : 'All courses are already in the list'}
                                    </div>
                                );
                            }

                            return (
                                <table className="table table-sm table-hover mb-0">
                                    <thead className="sticky-top bg-light">
                                        <tr>
                                            <th style={{ width: '10%' }}>ID</th>
                                            <th style={{ width: '60%' }}>Name</th>
                                            <th style={{ width: '15%' }}>Status</th>
                                            <th style={{ width: '15%' }} className="text-end">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map(course => (
                                            <tr key={course.id}>
                                                <td>{course.id}</td>
                                                <td className="text-truncate" style={{ maxWidth: 400 }}>{course.name}</td>
                                                <td>{course.status}</td>
                                                <td className="text-end">
                                                    <Button
                                                        label="Add"
                                                        icon="pi pi-plus"
                                                        className="p-button-sm p-button-success"
                                                        onClick={() => addCourse(course.id)}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            );
                        })()}
                    </div>

                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button
                            label="Close"
                            icon="pi pi-times"
                            className="p-button-outlined"
                            onClick={() => {
                                setShowAddDialog(false);
                                setSearchTerm('');
                            }}
                        />
                    </div>
                </Dialog>
            </div>
        </div>
    );
};
